import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { flag, fmtData, fmtEUR, countryName } from "../../lib/format";
import { ArrowClockwise, Star } from "@phosphor-icons/react";

export default function AdminPlans() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/plans/sync");
      setMsg(`${t("admin.synced")}: ${data.synced} (${data.mode})`);
      await load();
      setTimeout(() => setMsg(""), 3000);
    } finally { setSyncing(false); }
  };

  const update = async (id, payload) => {
    const { data } = await api.patch(`/admin/plans/${id}`, payload);
    setPlans((ps) => ps.map((p) => (p.id === id ? data : p)));
  };

  return (
    <div data-testid="admin-plans">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">{t("admin.catalog")}</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest">{t("admin.catalog")}</h1>
        </div>
        <div className="flex items-center gap-3">
          {msg && <span className="text-sm text-success">{msg}</span>}
          <button onClick={sync} disabled={syncing} className="btn-primary !py-2 !px-4 text-sm" data-testid="admin-sync-plans">
            <ArrowClockwise size={16} weight="bold" /> {syncing ? t("common.loading") : t("admin.syncPlans")}
          </button>
        </div>
      </div>

      <div className="card-soft overflow-auto !rounded-3xl">
        <table className="w-full text-sm" data-testid="admin-plans-table">
          <thead className="bg-cobalt-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">{t("common.country")}</th>
              <th className="text-left px-4 py-3">{t("common.plans")}</th>
              <th className="text-left px-4 py-3">{t("common.data")}</th>
              <th className="text-left px-4 py-3">{t("admin.wholesale")}</th>
              <th className="text-left px-4 py-3">{t("admin.markup")}</th>
              <th className="text-left px-4 py-3">{t("admin.manualPrice")}</th>
              <th className="text-left px-4 py-3">{t("admin.retail")}</th>
              <th className="text-left px-4 py-3">{t("admin.featured")}</th>
              <th className="text-left px-4 py-3">{t("admin.enabled")}</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t border-ink-100" data-testid={`admin-plan-${p.id}`}>
                <td className="px-4 py-3">{flag(p.country_iso2)} {countryName(p.country_iso2, i18n.language, p.country)}</td>
                <td className="px-4 py-3">{p.name}</td>
                <td className="px-4 py-3">{fmtData(p.data_mb)} · {p.days}d</td>
                <td className="px-4 py-3 text-ink-500">{fmtEUR(p.wholesale_price)}</td>
                <td className="px-4 py-3">
                  <input
                    type="number" step="0.1" min="1" defaultValue={p.markup}
                    onBlur={(e) => update(p.id, { markup: parseFloat(e.target.value) })}
                    className="w-20 border border-black/20 px-2 py-1 text-sm"
                    data-testid={`markup-${p.id}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number" step="0.1" min="0" defaultValue={p.manual_price || ""}
                    placeholder="—"
                    onBlur={(e) => update(p.id, { manual_price: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-24 border border-black/20 px-2 py-1 text-sm"
                    data-testid={`manual-${p.id}`}
                  />
                </td>
                <td className="px-4 py-3 font-bold">{fmtEUR(p.retail_price)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => update(p.id, { is_featured: !p.is_featured })}
                    className={p.is_featured ? "text-warning" : "text-ink-300"}
                    data-testid={`feature-${p.id}`}
                  >
                    <Star size={20} weight={p.is_featured ? "fill" : "regular"} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => update(p.id, { is_enabled: !p.is_enabled })}
                    className={`text-xs px-2 py-1 border ${p.is_enabled ? "border-success/30 bg-success/10 text-success" : "border-ink-200 bg-ink-100 text-ink-500"}`}
                    data-testid={`enable-${p.id}`}
                  >
                    {p.is_enabled ? "ON" : "OFF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
